import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { display } from 'styled-system';
import { compose } from 'recompose';
import { withTranslation } from 'react-i18next';
import urljoin from 'url-join';
import log from 'loglevel';
import { useHistory } from 'react-router';

import { useConfig } from 'Context/ConfigContext';
import { PushNotifications } from 'Util/Capacitors/SkooldioCapacitor';
import { CapacitorUtils, useCapacitor } from 'Util/Capacitors';
import fetch2, { METHOD } from 'Util/fetch';
import useMyExternalProfile from 'Routes/Classroom/Domain/useMyExternalProfile';

import { Flex } from '../Base';
import { withAnnouncement } from '../../HOC/Announcement';
import { withUsername } from '../../Redux/user-profile/hoc';
import { useNotificationTracking } from '../../Util/Analytics';

import Inbox from './Inbox';
import NotificationIcon from './NotificationIcon';
import {
  getUnreadCount,
  splitTab,
  ANNOUNCEMENT_CATEGORY,
  COURSE_ANNOUNCEMENT_CATEGORIES,
} from './utils';

const Container = styled(Flex)`
  height: ${(props) => props.height};
  align-items: center;
`;

const ResponsiveBox = styled(Flex)`
  position: relative;
  align-items: center;
  height: 100%;
  ${display}
`;

function isUrlValid(string) {
  try {
    new URL(string);
    return true;
  } catch (err) {
    return false;
  }
}

const Notification = ({ height, announcementContext, t, username, iconProps, ...restProps }) => {
  const { native = {}, myCourses } = useConfig();
  const { enablePushNotification, enablePersonalizedNotification } = native;

  const [news, setNews] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [courses, setCourses] = useState([]);
  const [isRedirecting, setIsRedirecting] = useState(false);

  const { logNotificationClick } = useNotificationTracking();
  const [isFromPushNotification, setIsFromPushNotification] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const {
    announcements: rawAnnouncements,
    markNotificationAsRead,
    hasMoreAnnouncements,
    loadMoreAnnouncements,
    loading,
  } = announcementContext;

  const history = useHistory();
  const { isNative } = CapacitorUtils;
  const { userApi, tungKai } = useConfig();
  const { openUrl, openInApp } = useCapacitor();
  const { data } = useMyExternalProfile({ fetchPolicy: 'cache-only' });

  const tungKaiDomainRegex =
    tungKai?.domainRegex ?? /^https?:\/\/tungkai\.[a-z0-9-.]*onlineathome\.ondemand\.in\.th/i;

  const getToken = useCallback(async () => {
    const providerName = data?.myExternalProfile?.providerName;
    return await fetch2(
      urljoin(userApi, `/me/externalToken?providerName=${providerName}`),
      null,
      METHOD.GET,
      null,
      {
        isRedirect: false,
        includeCredentials: true,
      }
    );
  }, [data?.myExternalProfile?.providerName, userApi]);

  const handleRedirectToClear = async () => {
    const clearRedirectUrlConfig = myCourses?.clearRedirectUrl ?? '';
    if (clearRedirectUrlConfig) {
      setIsRedirecting(true);
      let fullURL = clearRedirectUrlConfig;
      const hasTokenWithLink = !!fullURL.match('{token}');

      if (hasTokenWithLink) {
        const token = await getToken();
        fullURL = token ? clearRedirectUrlConfig.replace('{token}', token) : clearRedirectUrlConfig;
        await openUrl(fullURL, () => window.open(fullURL, '_blank'));
        await setIsRedirecting(false);
      }
    }
  };

  const handleRedirectUrl = async (redirectUrl) => {
    // ์NOTE: Support internal and external redirectUrl
    // NOTE: External URL without auth
    // NOTE: ถ้าเคส external service ที่ต้องมีการ auth จะต้อง handle เพิ่ม
    if (redirectUrl) {
      if (tungKaiDomainRegex.test(redirectUrl)) {
        openInApp(redirectUrl);
      } else if (isUrlValid(redirectUrl)) {
        await openUrl(redirectUrl, () => window.open(redirectUrl, '_blank'));
      } else {
        // NOTE: Internal redirect
        history.push(redirectUrl);
      }
    } else {
      // NOTE: ปล่อยให้ clear เดิมเป็นแบบนี้ไป เพื่อไม่ต้องไปปรับระบบเก่าที่อยู่ใน self เพื่อส่งข้อมูลแบบใหม่
      await setIsMenuOpen(true);
      await setIsFromPushNotification(true);
      await handleRedirectToClear();
      await setIsMenuOpen(false);
    }
  };

  useEffect(() => {
    if (isNative && enablePushNotification && enablePersonalizedNotification) {
      const registrationListener = PushNotifications.addListener('registration', (token) => {
        log.debug('Push registration success, token: ' + token.value);
      });
      const errorListener = PushNotifications.addListener('registrationError', (error) => {
        log.error('Error on registration: ' + JSON.stringify(error));
      });
      const receivedListener = PushNotifications.addListener(
        'pushNotificationReceived',
        (notification) => {
          log.debug('Push received: ' + JSON.stringify(notification));
        }
      );
      const actionListener = PushNotifications.addListener(
        'pushNotificationActionPerformed',
        async (notification) => {
          log.debug('Push action performed: ' + JSON.stringify(notification));
          const parsedMetadata = JSON.parse(notification.notification.data.metadata);
          const notificationId = notification.notification.data.notificationId;
          await markNotificationAsRead([notificationId]);
          log.debug('Notification marked as read successfully:', notificationId);
          handleRedirectUrl(parsedMetadata.redirectUrl);
        }
      );

      return () => {
        registrationListener.remove();
        errorListener.remove();
        receivedListener.remove();
        actionListener.remove();
      };
    }
  }, []);

  const filteredAnnouncements = rawAnnouncements.filter(
    (announcement) =>
      announcement?.category === ANNOUNCEMENT_CATEGORY.NEWS ||
      announcement?.category === ANNOUNCEMENT_CATEGORY.REMINDER ||
      announcement?.category === ANNOUNCEMENT_CATEGORY.CLEARS ||
      announcement?.category === ANNOUNCEMENT_CATEGORY.INTERNAL_OR_EXTERNAL_URL ||
      COURSE_ANNOUNCEMENT_CATEGORIES.includes(announcement?.category)
  );
  const unreadNo = getUnreadCount(filteredAnnouncements, username);

  useEffect(() => {
    if (!isFromPushNotification) return;
    if (filteredAnnouncements.length > 0) {
      updateAnnouncement();
      setIsFromPushNotification(false);
    }
  }, [filteredAnnouncements]);

  useEffect(() => {
    updateAnnouncement();
  }, [rawAnnouncements, t]);

  const updateAnnouncement = () => {
    const sortedData = rawAnnouncements.sort(
      (a, b) => new Date(b.publishedTime).getTime() - new Date(a.publishedTime).getTime()
    );
    const { news, reminders, courses } = splitTab(sortedData, t, username);
    setNews(news);
    setReminders(reminders);
    setCourses(courses);
  };

  const handleOnReadNotification = () => {
    updateAnnouncement();
  };

  return (
    <Container height={height} {...restProps}>
      <ResponsiveBox>
        <NotificationIcon
          unreadNo={unreadNo}
          onClick={() => {
            setIsMenuOpen(true);
            logNotificationClick();
          }}
          {...iconProps}
        />
        <Inbox
          open={isMenuOpen}
          onClose={() => setIsMenuOpen(false)}
          onReadNotification={() => handleOnReadNotification()}
          news={news}
          reminders={reminders}
          courses={courses}
          onRedirectUrl={handleRedirectUrl}
          isRedirecting={isRedirecting}
          loading={loading}
          hasMoreNews={hasMoreAnnouncements}
          onLoadMoreNews={loadMoreAnnouncements}
        />
      </ResponsiveBox>
    </Container>
  );
};

Notification.propTypes = {
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  iconProps: PropTypes.object,
};

Notification.defaultProps = {
  height: 0,
  iconProps: {},
};

export default compose(withTranslation('inbox'), withAnnouncement, withUsername)(Notification);
